import * as d3 from 'd3';
import type { I18n } from '@learnforge/i18n';

export interface TreeNode {
  labelKey: string;
  children?: Array<{ probability: number; node: TreeNode }>;
}

export interface TreeHandle {
  element: SVGSVGElement;
  update(root: TreeNode): void;
  destroy(): void;
}

/**
 * Adjust child at `index` to `newProbability`, rescaling siblings so the row
 * sums to 1. If all siblings (including the edited one) are zero, equal-split.
 */
export function rebalance(
  children: Array<{ probability: number; node: TreeNode }>,
  index: number,
  newProbability: number,
): void {
  const clamped = Math.max(0, Math.min(1, newProbability));
  children[index]!.probability = clamped;
  const others = children.filter((_, i) => i !== index);
  const remaining = 1 - clamped;
  const sumOthers = others.reduce((s, c) => s + c.probability, 0);
  if (sumOthers > 0) {
    const scale = remaining / sumOthers;
    for (let i = 0; i < children.length; i++) {
      if (i === index) continue;
      children[i]!.probability *= scale;
    }
  } else {
    const share = others.length > 0 ? remaining / others.length : 0;
    for (let i = 0; i < children.length; i++) {
      if (i === index) continue;
      children[i]!.probability = share;
    }
  }
}

export function leafProbabilities(root: TreeNode): number[] {
  const out: number[] = [];
  function walk(node: TreeNode, acc: number) {
    if (!node.children || node.children.length === 0) {
      out.push(acc);
      return;
    }
    for (const child of node.children) walk(child.node, acc * child.probability);
  }
  walk(root, 1);
  return out;
}

export interface CreateTreeOptions {
  container: HTMLElement;
  i18n: I18n<string>;
  initial: TreeNode;
  onChange?(root: TreeNode): void;
}

export function createTree(opts: CreateTreeOptions): TreeHandle {
  let root: TreeNode = opts.initial;

  const svg = d3
    .create('svg')
    .attr('class', 'pb-tree-diagram')
    .attr('viewBox', '0 0 720 400')
    .node()!;
  opts.container.appendChild(svg);

  function update(next: TreeNode) {
    root = next;
    render();
  }

  function render() {
    d3.select(svg).selectAll('*').remove();
    const g = d3.select(svg).append('g');
    layoutAndDraw(g, root, 360, 30, 320, 80, opts.i18n, opts);
  }

  render();

  return {
    element: svg,
    update,
    destroy() {
      svg.remove();
    },
  };
}

function layoutAndDraw(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  node: TreeNode,
  x: number,
  y: number,
  hSpan: number,
  vStep: number,
  i18n: I18n<string>,
  opts: CreateTreeOptions,
): void {
  g.append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('text-anchor', 'middle')
    .attr('class', 'pb-tree-node')
    .text(i18n.t(node.labelKey));
  if (!node.children || node.children.length === 0) return;
  const childCount = node.children.length;
  const childY = y + vStep;
  node.children.forEach((child, i) => {
    const childX = x + ((i - (childCount - 1) / 2) * hSpan) / Math.max(childCount, 1);
    g.append('line')
      .attr('x1', x)
      .attr('y1', y + 8)
      .attr('x2', childX)
      .attr('y2', childY - 12)
      .attr('class', 'pb-tree-edge');
    g.append('text')
      .attr('x', (x + childX) / 2)
      .attr('y', (y + childY) / 2)
      .attr('text-anchor', 'middle')
      .attr('class', 'pb-tree-prob')
      .text(child.probability.toFixed(2))
      .on('click', () => {
        const raw = prompt('Probability (0–1)?', String(child.probability));
        if (raw == null) return;
        const v = Number(raw);
        if (!Number.isFinite(v)) return;
        rebalance(node.children!, i, v);
        opts.onChange?.({ ...node });
      });
    layoutAndDraw(g, child.node, childX, childY, hSpan / 2, vStep, i18n, opts);
  });
}
